// Role-based permissions for VIDOC.
//
// Three roles:
//   - admin       → full access (everything)
//   - responsable → operations (depot/restitution/delivrance) + full client management
//   - expert      → only depot + restitution + create-new-client (no client browsing)
//
// Legacy "user" role is treated as "responsable" for backward compatibility.

export const ROLES = {
  ADMIN: "admin",
  RESPONSABLE: "responsable",
  EXPERT: "expert",
};

const NORMALIZE = (role) => {
  if (role === "user") return ROLES.RESPONSABLE; // legacy
  return role || ROLES.RESPONSABLE;
};

/**
 * Returns true if the given user can perform the given action.
 *
 * Actions:
 *   - depot, restitution            → create deposit / restitution
 *   - delivrance                    → create delivery
 *   - client.create                 → open the new-client form (modal or page)
 *   - client.list                   → browse the global client list (PII roster)
 *   - client.read                   → open a single client file (/clients/:id)
 *   - client.update                 → edit / archive / delete a client
 *   - client.import                 → bulk-import clients
 *   - search.global                 → access the global search page
 *   - admin                         → admin sections (users, icons, backup, purge, incidents)
 *   - queue                         → access the offline queue page
 */
export function can(user, action) {
  const role = NORMALIZE(user?.role);
  if (role === ROLES.ADMIN) return true;

  if (role === ROLES.RESPONSABLE) {
    return [
      "depot",
      "restitution",
      "delivrance",
      "client.create",
      "client.list",
      "client.read",
      "client.update",
      "client.import",
      "search.global",
      "queue",
    ].includes(action);
  }

  if (role === ROLES.EXPERT) {
    // Expert can open individual client files (each open is silently
    // counted; after EXPERT_CLIENT_VIEW_LIMIT opens, an alert is silently
    // sent to the admin) but CANNOT browse the global client list.
    return [
      "depot",
      "restitution",
      "client.create",
      "client.read",
      "queue",
    ].includes(action);
  }

  return false;
}

/** Human-readable label for the role badge. */
export function roleLabel(role) {
  const r = NORMALIZE(role);
  if (r === ROLES.ADMIN) return "Administrateur";
  if (r === ROLES.RESPONSABLE) return "Responsable";
  if (r === ROLES.EXPERT) return "Expert";
  return "Utilisateur";
}

/** Expert-only: max client file views before silently alerting the admin. */
export const EXPERT_CLIENT_VIEW_LIMIT = 5;
